import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconPlus } from '@tabler/icons-react';
import { inputStyle, fmtDateTime } from '../contracts/contractsApi';
import { createProject, PROJECT_STATUSES } from './projectsApi';

const STATUS_LABEL = Object.fromEntries(PROJECT_STATUSES);

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => onSnapshot(collection(db, 'clientProjects'), snap => {
    setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.id.localeCompare(a.id)));
  }, e => setError(e.message)), []);
  // Unseen client activity per project (cleared on the project page).
  const [activity, setActivity] = useState({});
  useEffect(() => onSnapshot(query(collection(db, 'hqNotifications'), where('readAt', '==', null)), snap => {
    const n = {};
    snap.docs.forEach(d => { const pid = d.data().projectId; n[pid] = (n[pid] || 0) + 1; });
    setActivity(n);
  }, () => setActivity({})), []);

  const create = async () => {
    if (!name.trim() || !clientName.trim()) { setError('Enter a project name and a client.'); return; }
    setBusy('create'); setError(null);
    try {
      const id = await createProject({ name: name.trim(), clientName: clientName.trim() });
      navigate(`/projects/${id}`);
    } catch (e) { setError(e.message); setBusy(null); }
  };

  const togglePortal = async (p) => {
    const enabled = !p.portalEnabled;
    if (enabled && !window.confirm(`Open the portal for ${p.clientName}? Invited client users will be able to see this project.`)) return;
    setBusy(p.id); setError(null);
    try { await httpsCallable(functions, 'setPortalEnabled')({ projectId: p.id, enabled }); }
    catch (e) { setError(e.message); }
    setBusy(null);
  };

  const cols = '110px 1.4fr 1fr 90px 100px 140px';
  return (
    <Layout>
      <PageHeader title="Client projects" subtitle="Custom builds and their client delivery portals"
        actions={<Button icon={<IconPlus size={14} stroke={2} />} onClick={() => setAdding(a => !a)}>New project</Button>} />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error && <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12 }}>{error}</div>}
        {adding && (
          <Card>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input style={{ ...inputStyle, width: 300 }} placeholder="Project name, e.g. Forestry Management Platform" value={name} onChange={e => setName(e.target.value)} />
              <input style={{ ...inputStyle, width: 240 }} placeholder="Client, e.g. The Forestry Company" value={clientName} onChange={e => setClientName(e.target.value)} />
              <Button loading={busy === 'create'} onClick={create}>Create</Button>
              <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </Card>
        )}
        {projects && (
          <Card padding={0}>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '10px 16px', fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div>ID</div><div>Project</div><div>Client</div><div>Status</div><div>Portal</div><div>Last updated</div>
            </div>
            {projects.length === 0 && <div style={{ padding: '28px 16px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No projects yet.</div>}
            {projects.map((p, i) => (
              <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
                style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, padding: '11px 16px', alignItems: 'center', fontSize: 12, cursor: 'pointer', borderBottom: i === projects.length - 1 ? 'none' : '0.5px solid var(--color-border-light)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.id}</div>
                <div style={{ fontWeight: 500 }}>{p.name}{activity[p.id] > 0 && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 500, color: '#fff', background: 'var(--color-brand)', borderRadius: 999, padding: '1px 7px' }}>{activity[p.id]} new</span>}</div>
                <div style={{ color: 'var(--color-text-secondary)' }}>{p.clientName}</div>
                <div>{STATUS_LABEL[p.status] || p.status}</div>
                <div onClick={e => e.stopPropagation()}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', opacity: busy === p.id ? 0.5 : 1 }}>
                    <input type="checkbox" checked={!!p.portalEnabled} disabled={busy === p.id} onChange={() => togglePortal(p)} />
                    {p.portalEnabled ? 'Open' : 'Closed'}
                  </label>
                </div>
                <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{fmtDateTime(p.updatedAt?.toDate?.().toISOString())}</div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}
